import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Clock, AlertTriangle, Filter, ExternalLink } from "lucide-react";

const STATUS_BADGE = {
  active: { label: "Active", color: "bg-[#E8ECE7] text-[#166534]" },
  completed: { label: "Completed", color: "bg-[#F1F5F9] text-[#475569]" },
  overdue: { label: "Overdue", color: "bg-[#FEE2E2] text-[#991B1B]" },
  auto_closed: { label: "Auto-closed", color: "bg-[#FEF3C7] text-[#92400E]" },
};

export default function AdminFieldCheckins() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(""); // empty = all

  const load = () => {
    setLoading(true);
    const params = status ? { status } : {};
    api.get("/admin/field/check-ins", { params })
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const active = rows.filter((r) => r.status === "active").length;
  const overdue = rows.filter((r) => r.status === "overdue").length;

  return (
    <div className="space-y-6" data-testid="admin-field-checkins-page">
      <div>
        <div className="hw-eyebrow mb-2">Operations</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Field check-ins</h1>
        <p className="text-[#334155] mt-2 max-w-2xl">
          Live view of who&apos;s in the field. Check-ins older than 4 hours without a check-out are
          flagged as <strong>Overdue</strong> and email admins automatically.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Active right now" value={active} accent="green" testId="checkin-stat-active" />
        <Stat label="Overdue" value={overdue} accent={overdue > 0 ? "red" : "neutral"} testId="checkin-stat-overdue" />
        <Stat label="Total shown" value={rows.length} accent="navy" testId="checkin-stat-total" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-[#1E3A8A]" />
        <label className="text-sm text-[#334155]">Status:</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="checkin-status-filter"
          className="px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-sm"
        >
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="overdue">Overdue</option>
          <option value="completed">Completed</option>
          <option value="auto_closed">Auto-closed</option>
        </select>
      </div>

      {loading && rows.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl h-40 animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="checkin-empty">
          <MapPin className="w-12 h-12 mx-auto text-[#1E3A8A] opacity-50 mb-3" strokeWidth={1.5} />
          <p className="text-[#334155]">No check-ins match this filter.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="checkin-list">
          {rows.map((r) => (
            <CheckinRow key={r.id} ci={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

const Stat = ({ label, value, accent, testId }) => {
  const tone = {
    green: "border-[#7E937D] bg-[#E8ECE7]",
    red: "border-[#FCA5A5] bg-[#FEE2E2]",
    navy: "border-[#1E3A8A]/30 bg-[#E0E7FF]",
    neutral: "border-[#E5E5E0] bg-white",
  }[accent || "neutral"];
  return (
    <div data-testid={testId} className={`rounded-2xl border ${tone} p-4`}>
      <div className="hw-eyebrow text-[#1E3A8A]">{label}</div>
      <div className="text-2xl font-display font-semibold text-[#0F172A] mt-1">{value}</div>
    </div>
  );
};

const CheckinRow = ({ ci }) => {
  const badge = STATUS_BADGE[ci.status] || STATUS_BADGE.completed;
  const loc = ci.manual_label || (ci.latitude && ci.longitude
    ? `${ci.latitude.toFixed(4)}, ${ci.longitude.toFixed(4)}`
    : (ci.city ? `${ci.city}${ci.region ? `, ${ci.region}` : ""}` : "Unknown"));
  return (
    <li data-testid={`checkin-row-${ci.id}`} className="bg-white border border-[#E5E5E0] rounded-2xl px-5 py-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-medium text-[#0F172A]">{ci.user_name || ci.user_email}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.color}`}>
              {badge.label}
            </span>
            {ci.status === "overdue" && (
              <AlertTriangle className="w-3.5 h-3.5 text-[#991B1B]" />
            )}
            <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A] bg-[#E0E7FF] px-2 py-0.5 rounded-full">
              {ci.location_method}
            </span>
          </div>
          <div className="text-sm text-[#334155] flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {loc}
              {ci.county && ` · ${ci.county} County`}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> In {formatTime(ci.check_in_at)}
              {ci.check_out_at && ` · Out ${formatTime(ci.check_out_at)}`}
              {ci.duration_min != null && ` · ${formatDuration(ci.duration_min)}`}
            </span>
            {ci.linked_type && ci.linked_id && (
              <span className="inline-flex items-center gap-1 text-[#1E3A8A]">
                <ExternalLink className="w-3.5 h-3.5" />
                Linked to {ci.linked_type === "outreach" ? "outreach event" : "care seeker"}
              </span>
            )}
          </div>
          {ci.note && <p className="text-sm text-[#334155] mt-2 whitespace-pre-wrap">{ci.note}</p>}
          {ci.check_out_note && <p className="text-xs text-[#1E3A8A] mt-1 italic">Check-out: {ci.check_out_note}</p>}
          {ci.ip && <p className="text-[10px] text-[#94A3B8] mt-1 font-mono">IP {ci.ip}</p>}
        </div>
      </div>
    </li>
  );
};

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
function formatDuration(mins) {
  if (mins == null) return "";
  if (mins < 60) return `${mins.toFixed(0)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}
